/**
 * 端末証明書(R7e)の問い合わせと書き込み。
 *
 * 書き込みは idp:operator 以上(**真実は API 側**。画面は導線の出し分けだけ)。
 * 発行の応答に入る `p12_password` は**どのキャッシュにも残さない**
 * (mutation の戻りをそのまま画面の state に渡して、閉じたら捨てる)。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import type { DeviceCertLang, DeviceCertOs } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText } from "./hooks";

/** 端末証明書が動いたら、一覧・KPI・ユーザー詳細の小表・監査を引き直す。 */
export function useInvalidateDeviceCerts() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["idp", "device-certs"] });
    void qc.invalidateQueries({ queryKey: ["idp", "user-device-certs"] });
    void qc.invalidateQueries({ queryKey: ["idp", "device-cert-events"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** 成功したら引き直して、失敗はそのまま出す(全ての書き込みで同じ形)。 */
function useWrite<TArgs, TOut>(fn: (args: TArgs) => Promise<TOut>, label: string) {
  const invalidate = useInvalidateDeviceCerts();
  return useMutation({
    mutationFn: fn,
    // **応答をキャッシュに残さない**(自己レビュー 中 2)。再送の戻りには新しい
    // ダウンロード URL(= 1 回きりのトークン)が入る。画面を閉じたら消えるように
    // 保持時間を 0 にし、呼び出し側でも `reset()` する
    gcTime: 0,
    onSuccess: () => {
      invalidate();
      void message.success(`${label}した`);
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

/** 一覧と KPI。`q` はユーザー名・備考の部分一致、`status` / `os` は完全一致。 */
export function useDeviceCerts(
  params: { q?: string; status?: string; os?: string },
  enabled = true,
) {
  const api = useApi();
  const search = new URLSearchParams({
    ...(params.q ? { q: params.q } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.os ? { os: params.os } : {}),
  });
  return useQuery({
    queryKey: ["idp", "device-certs", params.q ?? "", params.status ?? "", params.os ?? ""],
    queryFn: () => api.idpDeviceCerts(search),
    // 一覧 API は operator 以上。viewer では**問い合わせない**(fix1 の低 2)
    enabled,
  });
}

/** 1 枚の操作履歴(履歴の Drawer を開いたときだけ読む)。 */
export function useDeviceCertEvents(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "device-cert-events", id],
    queryFn: () => api.idpDeviceCertEvents(id!),
    enabled: !!id,
  });
}

/** ユーザー詳細の小表。タブを開いたときだけ読む。 */
export function useUserDeviceCerts(userId: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "user-device-certs", userId],
    queryFn: () => api.idpUserDeviceCerts(userId!),
    enabled: !!userId && enabled,
  });
}

/**
 * メール文面のプレビュー。**保存済みの文面**で作るので、直した直後は保存してから見る。
 * `enabled` が真になったときだけ取る(画面を開いただけでは叩かない)。
 */
export function useDeviceCertMailPreview(
  params: { os: DeviceCertOs; lang: DeviceCertLang; userId?: string },
  enabled: boolean,
) {
  const api = useApi();
  const search = new URLSearchParams({
    os: params.os,
    lang: params.lang,
    ...(params.userId ? { user_id: params.userId } : {}),
  });
  return useQuery({
    queryKey: ["idp", "device-cert-mail-preview", params.os, params.lang, params.userId ?? ""],
    queryFn: () => api.idpDeviceCertMailPreview(search),
    enabled,
  });
}

/**
 * 発行。**成功メッセージは出さない**(パスワードを見せるダイアログが出るので、
 * その裏でトーストを重ねない)。呼び出し側が戻りを state に持って 1 回だけ見せる。
 */
export function useIssueDeviceCert() {
  const api = useApi();
  const invalidate = useInvalidateDeviceCerts();
  return useMutation({
    mutationFn: (body: {
      user_id: string;
      os: DeviceCertOs;
      note: string;
      mail_subject_note: string;
      send_mail: boolean;
      lang: DeviceCertLang;
    }) => api.issueIdpDeviceCert(body),
    // 戻りに p12 のパスワードが入る。**mutation のキャッシュに残さない**
    // (自己レビュー 中 2。画面は state で 1 回だけ見せて捨てる)
    gcTime: 0,
    onSuccess: () => invalidate(),
    onError: (e) => void message.error(errorText(e)),
  });
}

export function useRevokeDeviceCert() {
  const api = useApi();
  return useWrite(
    (args: { id: string; reason: string }) => api.revokeIdpDeviceCert(args.id, args.reason),
    "失効",
  );
}

/** メールを送り直す。**古いリンクは無効になる**(断り文は画面側)。 */
export function useMailDeviceCert() {
  const api = useApi();
  return useWrite(
    (args: { id: string; lang: DeviceCertLang }) => api.mailIdpDeviceCert(args.id, args.lang),
    "メールを送り直",
  );
}

export function useRequestReplaceDeviceCert() {
  const api = useApi();
  return useWrite(
    (args: { id: string; note: string }) => api.requestReplaceIdpDeviceCert(args.id, args.note),
    "入替リクエストを出",
  );
}

/** 備考・メール件名メモだけを直す(表の中で直せる)。 */
export function useUpdateDeviceCert() {
  const api = useApi();
  return useWrite(
    (args: { id: string; note?: string; mail_subject_note?: string }) =>
      api.updateIdpDeviceCert(args.id, {
        ...(args.note !== undefined ? { note: args.note } : {}),
        ...(args.mail_subject_note !== undefined
          ? { mail_subject_note: args.mail_subject_note }
          : {}),
      }),
    "メモを保存",
  );
}
